import { useQuery } from "@tanstack/react-query";
import {
  VentureCard,
  VentureCardContent,
  VentureCardHeader,
  VentureCardTitle,
} from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { FathomKeyForm } from "@/components/team/FathomKeyForm";
import { supabase } from "@/integrations/supabase/client";

/**
 * /closer/fathom — Closer self-connect page for Fathom (D-13, D-18).
 *
 * Loads the logged-in user's own `closers` row (RLS-enforced via
 * `.eq('user_id', auth.uid())`) and renders the shared FathomKeyForm.
 * Edge-function `fathom-key-validate` accepts this closer_id per the
 * verifyAdminOrCloserSelf dual-mode auth (plan 16-05 Task 3).
 */
export default function Fathom() {
  const { data: closer, isLoading, refetch } = useQuery({
    queryKey: ["my-closer-row"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await supabase
        .from("closers")
        .select("id, name, fathom_api_key_vault_ref")
        .eq("user_id", user.id)
        .maybeSingle();
      return data as { id: string; name: string; fathom_api_key_vault_ref: string | null } | null;
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Fathom Recording</h1>
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!closer) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Fathom Recording</h1>
        <VentureCard>
          <VentureCardContent>
            <p className="text-muted-foreground">
              You are not linked as a closer. Contact your admin.
            </p>
          </VentureCardContent>
        </VentureCard>
      </div>
    );
  }

  const connected = !!closer.fathom_api_key_vault_ref;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Fathom Recording</h1>
        <p className="text-muted-foreground">
          Connect your Fathom account so demo recordings sync automatically.
        </p>
      </div>

      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle className="flex items-center justify-between">
            <span>Your Fathom Connection</span>
            {connected ? (
              <BadgeVenture variant="success">Connected</BadgeVenture>
            ) : (
              <BadgeVenture variant="warning">Not Connected</BadgeVenture>
            )}
          </VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          <FathomKeyForm closer={closer} onSuccess={() => refetch()} />
        </VentureCardContent>
      </VentureCard>
    </div>
  );
}
